import { Link } from 'react-router';
import { type CapiProductPage } from 'types/capi/pdp';

import Image from '~/components/Image';
import ProductCardInteractionEvent from '~/events/ProductCardInteractionEvent';
import usePageData from '~/hooks/usePageData';
import useProductCardImpression from '~/hooks/useProductCardImpression';
import useRegion from '~/hooks/useRegion';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import gtm from '~/services/gtm.client';

const MODULE_TYPE = 'pdp-more-colours-carousel';
const MODULE_POSITION = 1;

type Variant = CapiProductPage['variants'][number];

type ColourwayProps = {
  padImage: boolean;
  position: number;
  variant: Variant;
};

const Colourway = ({ padImage, position, variant }: ColourwayProps) => {
  const user = useUser();
  const { isBot } = useRootData();

  const impressionRef = useProductCardImpression({
    isBot,
    modulePosition: MODULE_POSITION,
    moduleType: MODULE_TYPE,
    position,
    product: variant
  });

  const sendGtmEvent = () => {
    if (variant.name)
      gtm.trackedButtonClick({
        buttonName: 'PRODUCT_CLICK',
        pagePosition: 'More Colours'
      });
  };

  const sendInteractionEvent = () => {
    new ProductCardInteractionEvent({
      gesture: 'click',
      guestId: user.guestId,
      isBot,
      modulePosition: MODULE_POSITION,
      moduleType: MODULE_TYPE,
      position,
      product: variant
    }).send();
  };

  return (
    <Link
      className="h-full w-auto shrink-0"
      to={variant.path}
      onClick={() => {
        sendGtmEvent();
        sendInteractionEvent();
      }}
    >
      <div
        ref={impressionRef}
        className="h-full overflow-hidden rounded-lg border border-concrete"
      >
        <Image
          className="h-full"
          src={variant.image.url}
          alt={variant.image.alt}
          width={150}
          height={150}
          pad={padImage}
          loading="eager"
        />
      </div>
    </Link>
  );
};

const MoreColours = () => {
  const product = usePageData<CapiProductPage>();
  const region = useRegion();

  if (!product.variants.length) return null;

  if (region.code !== 'GB') return null;

  return (
    <div className="mb-6 flex flex-col gap-6">
      <h2 className="text-title-sm">More colours</h2>
      <div className="custom-scrollbar -mx-4 flex h-20 gap-x-3 overflow-x-scroll px-4 sm:mx-0 sm:w-[552px] sm:px-0">
        {product.variants.map((variant, index) => (
          <Colourway
            key={variant.image.url}
            padImage={product.pad_images}
            position={index + 1}
            variant={variant}
          />
        ))}
      </div>
    </div>
  );
};

export default MoreColours;
